import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { getAbpLanguages } from './AbpBackend';
import { invalidateCachedTranslations } from './translationCache';
import { STORAGE_KEY, DEFAULT_LANGUAGE } from './constants';

export interface LanguageInfo {
  cultureName: string;
  displayName: string;
  flagIcon: string | null;
}

interface LanguageContextValue {
  currentLanguage: string;
  availableLanguages: LanguageInfo[];
  switchLanguage: (language: string) => Promise<void>;
  isChanging: boolean;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(
  undefined
);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [availableLanguages, setAvailableLanguages] = useState<LanguageInfo[]>(
    []
  );
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    const checkLanguages = () => {
      const langs = getAbpLanguages();
      if (langs && langs.length > 0) {
        setAvailableLanguages(
          langs
            .filter((l) => l.isEnabled !== false)
            .map((l) => ({
              cultureName: l.cultureName,
              displayName: l.displayName,
              flagIcon: l.flagIcon ?? null,
            }))
        );
      }
    };

    checkLanguages();
    i18n.on('loaded', checkLanguages);
    i18n.on('languageChanged', checkLanguages);
    // Retry after a delay in case languages arrive asynchronously
    const retryTimer = setTimeout(checkLanguages, 2000);
    return () => {
      i18n.off('loaded', checkLanguages);
      i18n.off('languageChanged', checkLanguages);
      clearTimeout(retryTimer);
    };
  }, [i18n]);

  const switchLanguage = useCallback(
    async (language: string) => {
      if (language === i18n.language) return;
      setIsChanging(true);
      try {
        await invalidateCachedTranslations(language);
        await i18n.changeLanguage(language);
        localStorage.setItem(STORAGE_KEY, language);
      } finally {
        setIsChanging(false);
      }
    },
    [i18n]
  );

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage: i18n.language || DEFAULT_LANGUAGE,
        availableLanguages,
        switchLanguage,
        isChanging,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
